import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Check, Plus, Bell, Search, Sparkles, MessageCircle } from "lucide-react";
import { authFetch } from "@/lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// NEXO-CONSULTOR Etapa 13 — chips proativos pós projects_create.
// Recebe lista de suggestions (id/label/tool/args/kind) e dispara
// POST /api/assistant/accept_suggestions ao clicar.
//
// Estado por chip: idle → loading → accepted (✓) | error (label vermelho).
// onAccepted(executed): callback opcional pra o parent atualizar state.
//
// BUGFIX-AGENT-INTEGRATION-PHASE2 (Sessão 3.2) — chips kind="setup_confirm"
// (e variações futuras como setup_deadline / setup_dates_confirm) NÃO
// chamam /accept_suggestions. Em vez disso, enviam o `label` como
// mensagem de chat via callback `onSendMessage`, que o backend
// intercepta em intercept_project_setup_reply (vanilla + streaming).

const KIND_ICON = {
  task: Plus,
  reminder: Bell,
  research: Search,
  setup_confirm: MessageCircle,
};

const CHAT_MESSAGE_KINDS = new Set(["setup_confirm"]);

export default function MessageSuggestions({ suggestions, onAccepted, onSendMessage }) {
  const [statusById, setStatusById] = useState({});
  const items = Array.isArray(suggestions) ? suggestions : [];

  const handleClick = useCallback(async (sug) => {
    const sid = sug.id;
    if (statusById[sid] === "loading" || statusById[sid] === "accepted") return;

    // Branch chat-message: sup_confirm e similares enviam o label como
    // turno do user. Backend roteia via intercept_project_setup_reply.
    if (CHAT_MESSAGE_KINDS.has(sug.kind)) {
      setStatusById((p) => ({ ...p, [sid]: "accepted" }));
      try {
        await onSendMessage?.(sug.label);
      } catch (e) {
        setStatusById((p) => ({ ...p, [sid]: "error" }));
      }
      return;
    }

    setStatusById((p) => ({ ...p, [sid]: "loading" }));
    try {
      const res = await authFetch(`${API_URL}/api/assistant/accept_suggestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ suggestion_ids: [sid] }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const exec = (data?.executed || []).find((e) => e.id === sid);
      if (exec?.ok) {
        setStatusById((p) => ({ ...p, [sid]: "accepted" }));
        onAccepted?.(exec);
      } else {
        setStatusById((p) => ({ ...p, [sid]: "error" }));
      }
    } catch {
      setStatusById((p) => ({ ...p, [sid]: "error" }));
    }
  }, [statusById, onAccepted, onSendMessage]);

  if (items.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="mt-2 flex flex-col gap-2 ml-2"
      data-testid="message-suggestions"
    >
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <Sparkles className="w-3 h-3" />
        <span>Próximos passos</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <AnimatePresence>
          {items.map((sug) => {
            const status = statusById[sug.id] || "idle";
            const Icon = KIND_ICON[sug.kind] || Plus;
            const accepted = status === "accepted";
            const loading = status === "loading";
            const error = status === "error";
            return (
              <motion.button
                key={sug.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.18 }}
                onClick={() => handleClick(sug)}
                disabled={loading || accepted}
                data-testid={`suggestion-chip-${sug.id}`}
                className={[
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium",
                  "border transition-all",
                  accepted
                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300 cursor-default"
                    : error
                    ? "bg-rose-500/10 border-rose-500/30 text-rose-300 hover:bg-rose-500/20"
                    : loading
                    ? "bg-nectar-card border-nectar-border text-slate-400 cursor-wait"
                    : "bg-nectar-card border-nectar-border text-slate-200 hover:bg-white/5 hover:border-brand-cyan/40 cursor-pointer",
                ].join(" ")}
              >
                {loading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : accepted ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <Icon className="w-3 h-3" />
                )}
                <span>{sug.label}</span>
              </motion.button>
            );
          })}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
